import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ScanSearch } from "lucide-react";
import { api } from "@/lib/api";
import { useProjectCtx } from "@/context/ProjectContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/EmptyState";
import { StatusPill } from "@/components/StatusPill";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { fmtDate } from "@/lib/format";
import { getErrorMessage } from "@/lib/errors";

export default function Alerts() {
  const { projectId, isProjectManager } = useProjectCtx();
  const [status, setStatus] = useState("OPEN");
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/alerts`, { params: status === "ALL" ? {} : { status } });
      setAlerts(data);
    } finally {
      setLoading(false);
    }
  }, [projectId, status]);

  useEffect(() => { load(); }, [load]);

  const act = async (id, next) => {
    setBusy(true);
    try {
      await api.patch(`/projects/${projectId}/alerts/${id}`, { status: next });
      toast.success(`Alert ${next.toLowerCase()}`);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const scan = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/alerts/scan`, {});
      toast.success(`Scan complete — ${data.created} new, ${data.escalated} escalated, ${data.resolved} resolved`);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="alerts-page">
      <PageHeader
        title="Alerts"
        description="Overdue drawings, materials and construction activities detected by the engine."
        action={
          <div className="flex items-center gap-2">
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="w-40" data-testid="alerts-status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All statuses</SelectItem>
                <SelectItem value="OPEN">Open</SelectItem>
                <SelectItem value="ACKNOWLEDGED">Acknowledged</SelectItem>
                <SelectItem value="RESOLVED">Resolved</SelectItem>
              </SelectContent>
            </Select>
            {isProjectManager && (
              <Button variant="outline" size="sm" disabled={busy} onClick={scan} data-testid="scan-alerts-button">
                <ScanSearch className="h-3.5 w-3.5" /> Scan now
              </Button>
            )}
          </div>
        }
      />

      <div className="rounded-xl border border-border bg-card">
        {!loading && alerts.length === 0 ? (
          <EmptyState title="No alerts" description="Nothing overdue for this filter." testId="alerts-empty-state" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow><TableHead>Severity</TableHead><TableHead>Type</TableHead><TableHead>Title</TableHead><TableHead>Due</TableHead><TableHead>Overdue</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {alerts.map((a) => (
                <TableRow key={a.id} data-testid={`alert-row-${a.id}`}>
                  <TableCell><StatusPill value={a.severity} /></TableCell>
                  <TableCell className="text-xs text-muted-foreground">{a.alert_type.replaceAll("_", " ")}</TableCell>
                  <TableCell className="text-foreground">{a.title}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{fmtDate(a.due_date)}</TableCell>
                  <TableCell className="font-mono text-xs text-amber-400">{a.days_overdue}d</TableCell>
                  <TableCell><StatusPill value={a.status} /></TableCell>
                  <TableCell className="text-right">
                    {a.status !== "RESOLVED" && (
                      <div className="flex justify-end gap-1.5">
                        {a.status === "OPEN" && (
                          <Button size="sm" variant="outline" disabled={busy} onClick={() => act(a.id, "ACKNOWLEDGED")} data-testid={`alert-acknowledge-btn-${a.id}`}>Ack</Button>
                        )}
                        <Button size="sm" variant="secondary" disabled={busy} onClick={() => act(a.id, "RESOLVED")} data-testid={`alert-resolve-btn-${a.id}`}>Resolve</Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}
